<template>
  <section class="module-section" :aria-labelledby="headingId">
    <header class="module-section-header">
      <h3 :id="headingId" class="module-section-heading">
        <i :class="headingIconClass" aria-hidden="true"></i>
        {{ title }} <small>({{ modules.length }})</small>
      </h3>
      <button
        v-if="collapsible"
        type="button"
        class="btn-icon section-collapse-toggle"
        :aria-expanded="expanded.toString()"
        :aria-controls="contentId"
        @click="expanded = !expanded"
      >
        <i :class="`fas fa-angle-${expanded ? 'up' : 'down'}`" aria-hidden="true"></i>
        <span class="sr-only">Toggle {{ title.toLowerCase() }} modules</span>
      </button>
    </header>

    <div v-show="expanded" :id="contentId">
      <empty-text-placeholder v-if="!modules.length" :empty-text="emptyText"></empty-text-placeholder>
      <ol v-else :id="listId" class="module-list">
        <template v-if="itemStyle === 'featured'">
          <curriculum-featured-module-card
            v-for="mod in visibleModules"
            :key="mod.id"
            :programmeModule="mod"
          ></curriculum-featured-module-card>
        </template>
        <template v-else>
          <curriculum-module-list-item
            v-for="mod in visibleModules"
            :key="mod.id"
            :programmeModule="mod"
            :variant="variant"
          ></curriculum-module-list-item>
        </template>
      </ol>

      <button
        v-if="hasHiddenModules"
        type="button"
        class="btn-link section-toggle"
        @click="showAll = !showAll"
        :aria-expanded="showAll.toString()"
        :aria-controls="listId"
        :aria-label="showAllToggleLabel"
      >
        {{ toggleText }}
        <i :class="`fas fa-angle-${showAll ? 'up' : 'right'}`" aria-hidden="true"></i>
      </button>
    </div>
  </section>
</template>

<style scoped>
.module-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 15px 0;
}

.module-section-heading small {
  color: var(--kate-type-alt);
  font-size: 0.85em;
}

.module-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.module-section-header button[aria-expanded="true"] {
  width: unset;
}

.module-section-header button:hover {
  color: var(--kate-type-link-hover);
}

.section-collapse-toggle {
  color: var(--kate-type-primary);
}

.module-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-toggle {
  align-items: center;
  justify-content: center;
  color: var(--kate-type-link);
  text-decoration: none;
  display: flex;
  gap: 5px;
  width: 100%;
}

.section-toggle:hover {
  text-decoration: underline;
}

.section-toggle:focus {
  color: var(--kate-type-link-hover);
}

</style>

<script>
import CurriculumModuleListItem from './curriculum-module-list-item.vue';
import CurriculumFeaturedModuleCard from './curriculum-featured-module-card.vue';
import EmptyTextPlaceholder from '../../components/empty-text-placeholder.vue';

export default {
  components: {
    CurriculumModuleListItem,
    CurriculumFeaturedModuleCard,
    EmptyTextPlaceholder,
  },

  props: {
    title: {
      type: String,
      required: true,
    },
    modules: {
      type: Array,
      default: () => [],
    },
    compactLimit: {
      type: Number,
      default: 2,
    },
    itemStyle: {
      type: String,
      default: 'list',
      validator: value => ['list', 'featured'].includes(value),
    },
    variant: {
      type: String,
      default: 'in-progress',
      validator: value => ['in-progress', 'upcoming', 'completed'].includes(value),
    },
    emptyText: {
      type: String,
      default: 'No modules in this section.',
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
    initiallyExpanded: {
      type: Boolean,
      default: true,
    },
  },

  beforeMount() {
    this.uuid = this.$.uid;
  },

  data() {
    return {
      expanded: this.initiallyExpanded,
      showAll: false,
      uuid: null,
    };
  },

  computed: {
    headingId() {
      return `curriculum-section-${this.uuid}-heading`;
    },

    contentId() {
      return `curriculum-section-${this.uuid}-content`;
    },
    listId() {
      return `curriculum-section-${this.uuid}-list`;
    },

    visibleModules() {
      if (this.showAll) {
        return this.modules;
      }
      return this.modules.slice(0, this.compactLimit);
    },

    hasHiddenModules() {
      return this.modules.length > this.compactLimit;
    },

    hiddenCount() {
      return Math.max(this.modules.length - this.compactLimit, 0);
    },

    toggleText() {
      if (this.showAll) {
        return 'View less';
      }
      return `View ${this.hiddenCount} more`;
    },
    showAllToggleLabel() {
      return `${this.toggleText} ${this.title} modules`;
    },

    headingIconClass() {
      if (this.variant === 'completed') {
        return 'far fa-book-open';
      }
      if (this.variant === 'upcoming') {
        return 'far fa-clock-three';
      }
      return 'fas fa-arrow-rotate-right';
    },
  },

};
</script>
